import { createRootRoute, createRoute, createRouter, Outlet } from "@tanstack/react-router";
import { OnePager } from "./pages/OnePager";
import { AdminLogin } from "./pages/AdminLogin";
import { AdminSection, AdminShell } from "./pages/AdminShell";
import { AdminToday } from "./pages/Today";
import { AdminContent, CONTENT_TABS, type ContentTab } from "./pages/Content";
import { PathEditor } from "./pages/PathEditor";
import { AdminQuestions } from "./pages/Questions";
import { AdminSettings } from "./pages/Settings";
import { AdminChurches } from "./pages/Churches";
import { AdminAttention } from "./pages/Attention";
import { AdminModeration, AdminPrayerWall } from "./pages/Moderation";
import { AdminFams } from "./pages/Fams";
import { AdminPulse } from "./pages/Pulse";
import { AdminPeople } from "./pages/People";
import { AdminNotifications, NOTIFICATION_TABS, type NotificationsTab } from "./pages/Notifications";
import { AdminRewards } from "./pages/Rewards";
import { AdminTeam } from "./pages/Team";
import { AdminDecisions } from "./pages/Decisions";
import { SentryErrorFallback } from "./components/SentryErrorFallback";

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  errorComponent: ({ reset }) => <SentryErrorFallback resetError={reset} />,
});

const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: OnePager });

const adminLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/login",
  validateSearch: (s: Record<string, unknown>): { step?: "totp" } => (s.step === "totp" ? { step: "totp" } : {}),
  component: AdminLogin,
});

const adminRoute = createRoute({ getParentRoute: () => rootRoute, path: "/admin", component: AdminShell });
const adminIndexRoute = createRoute({ getParentRoute: () => adminRoute, path: "/", component: AdminToday });

const contentRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/content",
  validateSearch: (s: Record<string, unknown>): { tab: ContentTab } => ({ tab: CONTENT_TABS.includes(s.tab as ContentTab) ? (s.tab as ContentTab) : "dailyWords" }),
  component: function Content() {
    const { tab } = contentRoute.useSearch();
    return <AdminContent tab={tab} />;
  },
});
const pathRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/content/paths/$id",
  component: function Path() {
    const { id } = pathRoute.useParams();
    return <PathEditor id={id} />;
  },
});
const questionsRoute = createRoute({ getParentRoute: () => adminRoute, path: "/questions", component: AdminQuestions });
const settingsRoute = createRoute({ getParentRoute: () => adminRoute, path: "/settings", component: AdminSettings });
const churchesRoute = createRoute({ getParentRoute: () => adminRoute, path: "/churches", component: AdminChurches });
const attentionRoute = createRoute({ getParentRoute: () => adminRoute, path: "/attention", component: AdminAttention });
const moderationRoute = createRoute({ getParentRoute: () => adminRoute, path: "/moderation", component: AdminModeration });
const prayerRoute = createRoute({ getParentRoute: () => adminRoute, path: "/prayer", component: AdminPrayerWall });
const famsRoute = createRoute({ getParentRoute: () => adminRoute, path: "/fams", component: AdminFams });
const pulseRoute = createRoute({ getParentRoute: () => adminRoute, path: "/pulse", component: AdminPulse });
const peopleRoute = createRoute({ getParentRoute: () => adminRoute, path: "/people", component: AdminPeople });
const notificationsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/notifications",
  validateSearch: (s: Record<string, unknown>): { tab: NotificationsTab } => ({
    tab: NOTIFICATION_TABS.includes(s.tab as NotificationsTab) ? (s.tab as NotificationsTab) : "notificationMatrix",
  }),
  component: function NotificationsPage() {
    const { tab } = notificationsRoute.useSearch();
    return <AdminNotifications tab={tab} />;
  },
});
const rewardsRoute = createRoute({ getParentRoute: () => adminRoute, path: "/rewards", component: AdminRewards });
const teamRoute = createRoute({ getParentRoute: () => adminRoute, path: "/team", component: AdminTeam });
const decisionsRoute = createRoute({ getParentRoute: () => adminRoute, path: "/decisions", component: AdminDecisions });

const adminSectionRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/$section",
  component: function Section() {
    const { section } = adminSectionRoute.useParams();
    return <AdminSection section={section} />;
  },
});

const routeTree = rootRoute.addChildren([indexRoute, adminLoginRoute, adminRoute.addChildren([adminIndexRoute, contentRoute, pathRoute, questionsRoute, settingsRoute, churchesRoute, attentionRoute, moderationRoute, prayerRoute, famsRoute, pulseRoute, peopleRoute, notificationsRoute, rewardsRoute, teamRoute, decisionsRoute, adminSectionRoute])]);

export const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
